import React, { FC } from 'react';

import dynamic from 'next/dynamic';
import cx from 'classnames';

import {
  Block,
  Blocks,
  ImageGridBlock,
  DefaultBlock,
  TitledTextBlock,
  CaseStudyGridBlock,
  TextBlock as TextBlockType,
  ContactCTABlock,
} from 'lib/sanity/types';

const Fallback = dynamic(() => import('blocks/Fallback'));
const ImageGrid = dynamic(() => import('blocks/ImageGrid'));
const TitledText = dynamic(() => import('blocks/TitledText'));
const CaseStudyGrid = dynamic(() => import('blocks/CaseStudyGrid'));
const TextBlock = dynamic(() => import('blocks/TextBlock'));
const ContactCTA = dynamic(() => import('blocks/ContactCTA'));

type Props = {
  contents?: Blocks | null;
  isLandingTab?: boolean;
  isBlog?: boolean;
  isCaseStudy?: boolean;
  isFromSnippet?: boolean;
  backgroundColor?: string;
  gridAspectRatio?: number;
  handleOnClick?: (title: string, subtitle?: string | null) => any;
};

const renderComponentForBlock = (
  block: Block,
  isCaseStudy?: boolean,
  isLandingTab?: boolean,
  isFromSnippet?: boolean,
  backgroundColor?: string,
  gridAspectRatio?: number,
  handleOnClick?: (title: string, subtitle?: string | null) => any,
  index?: number,
): React.ReactNode => {
  switch (block._type) {
    case 'contactCTABlock':
      return <ContactCTA block={block as ContactCTABlock} />;
    case 'dividerBlock':
      return (
        <div className="w-full tablet:px-content-narrow space-y-2 laptop:space-y-0">
          <hr />
        </div>
      );
    case 'imageGridBlock':
      return (
        <ImageGrid
          block={block as ImageGridBlock}
          isCaseStudy={isCaseStudy}
          isFromSnippet={isFromSnippet}
        />
      );
    case 'titledTextBlock':
      return (
        <TitledText
          block={block as TitledTextBlock}
          isCaseStudy={isCaseStudy}
          isLandingTab={isLandingTab}
          backgroundColor={backgroundColor}
        />
      );
    case 'caseStudyGridBlock':
      return (
        <CaseStudyGrid
          block={block as CaseStudyGridBlock}
          isLandingTab={isLandingTab}
          isFromSnippet={isFromSnippet}
          handleOnClick={handleOnClick}
          aspectRatio={gridAspectRatio}
        />
      );
    case 'textBlock':
      return (
        <TextBlock
          block={block as TextBlockType}
          isLandingTab={isLandingTab}
          isCaseStudy={isCaseStudy}
          index={index}
        />
      );
    default:
      return <Fallback block={block as DefaultBlock} />;
  }
};

const BlockSwitch: FC<Props> = ({
  contents,
  isCaseStudy,
  isLandingTab = false,
  isFromSnippet,
  backgroundColor,
  gridAspectRatio,
  handleOnClick,
}) => {
  return (
    <>
      {contents &&
        contents.map((block, index) => {
          return block.hide ? null : (
            <section
              key={block._key}
              className={cx({
                'flex flex-col items-end w-full mb-content-narrow tablet:mb-content-normal':
                  isLandingTab,
              })}
            >
              {renderComponentForBlock(
                block,
                isCaseStudy,
                isLandingTab,
                isFromSnippet,
                backgroundColor,
                gridAspectRatio,
                handleOnClick,
                index,
              )}
            </section>
          );
        })}
    </>
  );
};

export default BlockSwitch;
